import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { type ReactNode } from "react";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger, cn } from "ui";

import { type ExerciseComboboxItemProps } from "src/workouts/ExerciseCombobox";
import { ExercisePreviewImage } from "src/workouts/ExercisePreviewImage";
import { getExercisePreviewImage } from "src/workouts/getExercisePreviewImage";

export const ExerciseComboboxItem = observer(function ExerciseComboboxItem(
  props: ExerciseComboboxItemProps,
) {
  const exercisePreview = getExercisePreviewImage(props.exercise);

  const imagePreview = (
    <ExercisePreviewImage
      exercise={props.exercise}
      imageClassName={props.imageClassName}
    />
  );

  function renderContent(trigger: ReactNode) {
    const content = (
      <div
        className={cn(
          "flex items-center space-x-2 px-2 py-1.5 w-full",
          props.selected && "bg-zinc-100 border border-zinc-300 rounded-md",
          props.className,
        )}
      >
        {trigger}
        <div
          className={cn(
            "first-letter:capitalize truncate",
            props.selected && "font-medium text-zinc-900",
            props.exerciseNameClassName,
          )}
        >
          <span className="truncate">{props.exercise.name}</span>
        </div>
        {props.selected && (
          <Icon
            name="tabler-filled-circle-check"
            className="ml-auto h-4 w-4 shrink-0 text-zinc-600"
          />
        )}
        {props.loadingExerciseId === props.exercise.id && (
          <div className="ml-auto">
            <Icon name="tabler-loader-2" className="h-4 w-4 animate-spin" />
          </div>
        )}
      </div>
    );
    return content;
  }

  if (!exercisePreview) {
    return renderContent(imagePreview);
  }

  return (
    <Popover open={props.showPreview}>
      <PopoverTrigger asChild>{renderContent(imagePreview)}</PopoverTrigger>
      <PopoverAnchor virtualRef={props.popoverAnchorRef} />
      <PopoverContent
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        side={props.exercisePreviewSide ?? "left"}
        className="w-60"
      >
        <ExercisePreviewImage
          exercise={props.exercise}
          imageClassName="w-52 h-52"
        />
      </PopoverContent>
    </Popover>
  );
});
